import {
  getMarketingConsentPartners,
  getMarketingConsentServices,
  getMarketingConsentText,
} from '@axo/form/configs';
import { ReloadedFormData } from '@axo/form/data-access/backend-sync';
import { loan_application } from '@axo/shared/data-access/types';
import {
  reloadFrontendToggles,
  saveFrontendToggles,
} from '../../../../shared/mutators/frontendToggles';
import { removeCoAppData } from '../../../../shared/mutators/removeCoAppData';
import { transformIncome } from '../../../../shared/mutators/transformIncomes';
import {
  mapNumberInputsToString,
  mapRadioInputsToBoolean,
  mapRadioInputsToString,
  mapStringInputsToNumber,
} from '../../../../utils';
import type { DenmarkFormData } from '../FormData.dk.types';
import { debtIsFilled } from './utils/debtIsFilled';
import { getCleanDebts } from './utils/getCleanDebts';
import { getTotalRefinanceAmount } from './utils/getTotalRefinanceAmount';

const frontendToggles = [
  'Frontend/AddCoApplicant',
  'Frontend/HasLease',
  'Frontend/HasExtraIncome',
  'Frontend/HasCurrentYearDebt',
  'Frontend/HasDebtCollection',
] as const;

const booleanRadioInputs = [
  'LoanApplication/HasCars',
  'LoanApplication/OwnsHolidayHome',
  'LoanApplication/HasDankort',
  'LoanApplication/HasOtherPaymentCards',
  'LoanApplication/HasUnemploymentFund',
  'LoanApplication/PaymentRemarks',
  'LoanApplication/CoApplicantHasUnemploymentFund',
] as const;

const numericStringInputs = [
  'Car/0/TotalCostMonthly',
  'LoanApplication/ResidencePermit',
  'LoanApplication/RentCostMonthly',
  'LoanApplication/ExtraIncomeMonthly',
  'LoanApplication/LivingCostMonthly',
  'LoanApplication/NetIncomeMonthly',
  'LoanApplication/RegistrationNumber',
  'LoanApplication/CurrentYearDebtOutstandingAmount',
  'LoanApplication/YearsAsBankCustomer',
  'LoanApplication/CoApplicantResidencePermit',
] as const;

// Map data from Backend Values to Frontend Values
const fromApi = (data: Record<string, unknown>): DenmarkFormData => ({
  ...data,
  // Shared
  ...mapNumberInputsToString(data, numericStringInputs),
  ...mapRadioInputsToString(data, booleanRadioInputs),
  ...transformIncome(data, 'fromApi'),
  ...reloadFrontendToggles(data),
  // Market specific
  ...reloadExtraFrontendToggles(data),
  ...mapCitizenshipFromApi(data),
  ...mapChildrenFromApi(data),
  ...addZerosToRegNo(data),
  ...userHasChosenFlow(data),
  ...reloadDropdownPurpose(data),
});

const reloadDropdownPurpose = (data: ReloadedFormData) => {
  const result = {};

  if ('LoanApplication/Purpose' in data) {
    Object.assign(result, {
      'Frontend/DropdownPurpose': data['LoanApplication/Purpose'],
    });
  }

  return result;
};

const userHasChosenFlow = (data: ReloadedFormData) => {
  const result = {};

  // First element of Step 3, which means user has done chosen a flow in step 2
  if ('LoanApplication/MaritalStatus' in data) {
    Object.assign(result, {
      'Frontend/Flow': true,
    });
  }

  return result;
};

const mapCitizenshipFromApi = (data: ReloadedFormData) => {
  const citizenship = data['LoanApplication/Citizenship'];

  if (citizenship === undefined) return {};

  return {
    'Frontend/IsDanish': data['LoanApplication/Citizenship'] === 'DK',
  };
};

const mapChildrenFromApi = (data: ReloadedFormData) => {
  const result = {};

  if ('AmountOfChildren' in data) {
    Object.assign(result, {
      'Frontend/AmountOfChildren': data['AmountOfChildren'],
    });

    const regex = new RegExp(/^Child\/\d\/Age$/);

    for (const [key, value] of Object.entries(data)) {
      if (key.match(regex)) {
        Object.assign(result, {
          [key]: String(value),
        });
      }
    }
  }

  return result;
};

const addZerosToRegNo = (data: ReloadedFormData) => {
  const result = {};

  if ('LoanApplication/RegistrationNumber' in data) {
    Object.assign(result, {
      'LoanApplication/RegistrationNumber': String(
        data['LoanApplication/RegistrationNumber']
      ).padStart(4, '0'),
    });
  }

  return result;
};

const addStaticValues = {
  'LoanApplication/MarketCountry': 'DK',

  'Person/MarketCountry': 'DK',
  'Customer/MarketCountry': 'DK',
  'Customer/MobilePhoneCountryCode': 45,

  'CoPerson/MarketCountry': 'DK',
  'CoCustomer/MarketCountry': 'DK',
  'CoCustomer/MobilePhoneCountryCode': 45,
};

// TODO: remove when we are using the new consent service
// note, consent is a part of customer, but is handled in a separate handler
const addConsentFields = (data: DenmarkFormData) => {
  const result = {};

  if (data['Ignore/MarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/MarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('dk'),
        Partners: getMarketingConsentPartners('dk'),
        Services: getMarketingConsentServices('dk'),
      },
    });
  }

  if (data['Ignore/CoApplicantMarketingConsent']) {
    Object.assign(result, {
      'MarketingConsent/CoApplicantMarketingConsent': {
        Version: 1,
        ConsentText: getMarketingConsentText('dk'),
        Partners: getMarketingConsentPartners('dk'),
        Services: getMarketingConsentServices('dk'),
      },
    });
  }

  return result;
};

const reloadExtraFrontendToggles = (data: ReloadedFormData) => {
  const result = {
    'Frontend/AllDebtsFilled': true,
  };

  // Magic Token flow
  if (window && new URLSearchParams(window.location.search).get('token')) {
    Object.assign(result, {
      'Frontend/HasLease': 'No',
      'Frontend/HasExtraIncome': 'No',
      'Frontend/HasCurrentYearDebt': 'No',
      'Frontend/HasDebtCollection': 'No',
    });
  }

  if ('Car/0/TotalCostMonthly' in data) {
    if (Number(data['Car/0/TotalCostMonthly']) > 0) {
      Object.assign(result, {
        'Frontend/HasLease': 'Yes',
      });
    }
  }

  if ('LoanApplication/ExtraIncomeMonthly' in data) {
    Object.assign(result, {
      'Frontend/HasExtraIncome': 'Yes',
    });
  }

  if ('LoanApplication/CurrentYearDebtOutstandingAmount' in data) {
    Object.assign(result, {
      'Frontend/HasCurrentYearDebt': 'Yes',
    });
  }

  if (
    data['LoanApplication/Debts']?.some(
      (debt) => debt.Type === loan_application.DebtType.Collection
    )
  ) {
    Object.assign(result, {
      'Frontend/HasDebtCollection': 'Yes',
    });
  }

  if (data['LoanApplication/Debts']?.some((debt) => !debtIsFilled(debt))) {
    Object.assign(result, {
      'Frontend/AllDebtsFilled': undefined,
    });
  }

  return result;
};

// Map data from Frontend Values to Backend Values
const toApi = (data: DenmarkFormData): DenmarkFormData => ({
  ...data,
  // Shared
  ...mapRadioInputsToBoolean(data, booleanRadioInputs),
  ...mapStringInputsToNumber(data, numericStringInputs),
  ...saveFrontendToggles(data, frontendToggles),
  ...transformIncome(data, 'toApi'),
  ...removeCoAppData(data),
  // Market specific
  ...addStaticValues,
  ...addConsentFields(data),

  ...mapChildrenToApi(data),
  ...addCarType(data),
  ...addCitizenshipValues(data),
});

const mapChildrenToApi = (data: DenmarkFormData) => {
  const result = {};

  const regex = new RegExp(/^Child\/\d\/Age$/);

  for (const [key, value] of Object.entries(data)) {
    if (key.match(regex)) {
      Object.assign(result, {
        [key]: value === null ? null : Number(value),
      });
    }
  }

  return result;
};

const addCarType = (data: DenmarkFormData) => {
  const result = {};

  if (
    data['LoanApplication/HasCars'] === 'Yes' &&
    data['Frontend/HasLease'] === 'No'
  ) {
    Object.assign(result, {
      'Car/0/OwnershipType': loan_application.CarOwnerShipType.Owned,
    });
  }

  if (
    data['Car/0/TotalCostMonthly'] &&
    Number(data['Car/0/TotalCostMonthly']) > 0
  ) {
    Object.assign(result, {
      'Car/0/OwnershipType': loan_application.CarOwnerShipType.Leased,
    });
  }

  return result;
};

const addCitizenshipValues = (data: DenmarkFormData) => {
  const result = {};

  const isDanish = data['Frontend/IsDanish'];
  const addCoApplicant = data['Frontend/AddCoApplicant'];

  Object.assign(result, {
    'LoanApplication/Citizenship': isDanish
      ? 'DK'
      : data['LoanApplication/Citizenship'] || 'DK',
  });

  if (addCoApplicant) {
    const coAppIsDanish = data['Frontend/CoApplicantIsDanish'];

    Object.assign(result, {
      'LoanApplication/CoApplicantCitizenship': coAppIsDanish
        ? 'DK'
        : data['LoanApplication/CoApplicantCitizenship'],
      'LoanApplication/CoApplicantMaritalStatus':
        data['LoanApplication/MaritalStatus'],
    });
  }

  return result;
};

const toApiLastSync = (data: DenmarkFormData) => {
  const result: DenmarkFormData = {};

  const debts = data['LoanApplication/Debts'];
  const cleanDebts = getCleanDebts({ debts });
  const isTryingToRefinance =
    data['LoanApplication/Purpose'] ===
    loan_application.Purpose.RefinancingLoan;
  const hasNoDebts = !cleanDebts || cleanDebts.length === 0;
  const refinanceAmount = getTotalRefinanceAmount({
    debts: cleanDebts,
  });
  const hasOtherPurpose = data['LoanApplication/OtherPurpose'];
  const totalRefinanceAmountForm =
    data['LoanApplication/TotalRefinancingAmount'];
  const initialPurpose = data['LoanApplication/InitialPurpose'];

  Object.assign(result, {
    'LoanApplication/Debts': cleanDebts,
  });

  if (refinanceAmount > 0) {
    Object.assign(result, {
      'LoanApplication/Purpose': loan_application.Purpose.RefinancingLoan,
    });
  }

  if (isTryingToRefinance) {
    if (hasNoDebts || !refinanceAmount) {
      Object.assign(result, {
        'LoanApplication/Purpose':
          initialPurpose === loan_application.Purpose.RefinancingLoan
            ? loan_application.Purpose.SpecialOccasion
            : initialPurpose,
      });
    }

    if (refinanceAmount > 0 && totalRefinanceAmountForm !== refinanceAmount) {
      Object.assign(result, {
        'LoanApplication/TotalRefinancingAmount': refinanceAmount,
      });
    }
  }

  const resultPurpose = result?.['LoanApplication/Purpose'];
  if (hasOtherPurpose) {
    if (resultPurpose) {
      if (resultPurpose !== loan_application.Purpose.Other) {
        Object.assign(result, {
          'LoanApplication/OtherPurpose': null,
        });
      }
    } else if (
      data['LoanApplication/Purpose'] !== loan_application.Purpose.Other
    ) {
      Object.assign(result, {
        'LoanApplication/OtherPurpose': null,
      });
    }
  }

  if (!('LoanApplication/RentCostMonthly' in data)) {
    Object.assign(result, {
      'LoanApplication/RentCostMonthly': 0,
    });
  }

  return { ...data, ...result };
};

export const mutators = {
  fromApi,
  toApi,
  toApiLastSync,
};
